import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './Credentials.css';

interface Stat {
  number: number;
  suffix: string;
  titleNl: string;
  titleEn: string;
  descriptionNl: string;
  descriptionEn: string;
}

const stats: Stat[] = [
  {
    number: 4,
    suffix: '',
    titleNl: 'Sectoren',
    titleEn: 'Sectors',
    descriptionNl: 'Gespecialiseerd in Accounting, Audit, Tax & Legal.',
    descriptionEn: 'Specialized in accounting, audit, law and tax to find the right match.',
  },
  {
    number: 4,
    suffix: '',
    titleNl: 'Expert consultants',
    titleEn: 'Expert consultants',
    descriptionNl: 'Een compact team met senior ervaring in finance, tax, audit en legal search.',
    descriptionEn: 'A lean team with senior experience across finance, tax, audit, and legal search.',
  },
  {
    number: 25,
    suffix: '+',
    titleNl: 'Samenwerkende klanten',
    titleEn: 'Partnering clients',
    descriptionNl: 'langdurige partnerships variërend van kleine en middelgrote kantoren tot Big Four-organisaties en gespecialiseerde boutique-kantoren.',
    descriptionEn: 'Long-term partnerships with growth companies and mid-market firms that need to move fast.',
  },
  {
    number: 45,
    suffix: '+',
    titleNl: 'Kandidaten geplaatst',
    titleEn: 'Candidates placed',
    descriptionNl: 'In het afgelopen jaar succesvol bemiddeld voor onze klanten.',
    descriptionEn: 'In the past year placed successfully for scale-ups, mid-market firms, and niche boutiques.',
  },
];

const Credentials: React.FC = () => {
  const { language } = useLanguage();
  const [counters, setCounters] = useState<number[]>(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [hasAnimated]);

  const animateCounters = () => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    stats.forEach((stat, index) => {
      let currentStep = 0;
      const increment = stat.number / steps;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.floor(increment * currentStep), stat.number);

        setCounters((prev) => {
          const newCounters = [...prev];
          newCounters[index] = newValue;
          return newCounters;
        });

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  };

  return (
    <section className="credentials" ref={sectionRef}>
      <div className="credentials-container">
        <h2 className="credentials-title">
          {language === 'nl' ? 'Onze cijfers' : 'Our credentials'}
        </h2>

        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-number">
                {counters[index].toLocaleString()}{stat.suffix}
              </div>
              <h3 className="stat-title">
                {language === 'nl' ? stat.titleNl : stat.titleEn}
              </h3>
              <p className="stat-description">
                {language === 'nl' ? stat.descriptionNl : stat.descriptionEn}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Credentials;
